import { CircularProgressBar } from "@mdotm/shadcn/legacy/components";
import { unpromisify } from "@mdotm/shadcn/legacy/utils";
import { type SwitchProps, Switch } from "@mdotm/shadcn/mdotm/components";
import { useAsync } from "@mdotm/utils/promise";

/**
 * A `Switch` whose change is awaited, showing a spinner in place of the control while it is in
 * flight. Both call sites are in `UserSettingsPanel`, where flipping a toggle is a server round
 * trip (enable a user, enable a customer).
 *
 * This was `AsyncCheckbox` until MDOT-24854. It served a tick box and a toggle from one component
 * by forwarding the legacy `Checkbox`'s `switchType`, and both of its call sites passed
 * `"switch"` — so when that component was split in two, the checkbox half had no consumers left
 * and was deleted rather than kept as an untested export.
 *
 * Props are DERIVED from `SwitchProps` rather than restated. The old version hand-copied the
 * legacy discriminated union inline, deriving nothing from the component it wrapped, which is what
 * made it a trap during the migration: it kept advertising `switchType` and `switchPosition` after
 * the control had dropped them, with nothing to keep the two in step. `Omit` means a prop removed
 * upstream disappears here too.
 */
export type AsyncSwitchProps = Omit<SwitchProps, "onChange"> & {
	onChange(newValue: boolean): Promise<void>;
};

export function AsyncSwitch({ onChange, ...rest }: AsyncSwitchProps): React.JSX.Element {
	const [run, asyncState] = useAsync<void, [boolean]>(onChange);

	const handleChange = unpromisify(async (newValue: boolean) => {
		await run(newValue).catch(console.error);
	});

	return asyncState.kind === "loading" ? (
		<CircularProgressBar value="indeterminate" outerDiameter={12} />
	) : (
		<Switch onChange={handleChange} {...rest} />
	);
}
